import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Heart, Bed, Bath, Ruler, Fuel, Calendar } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { useI18n } from "@/contexts/I18nContext";
import { useFavorites } from "@/contexts/FavoritesContext";
import { getOfferLabel, getPricePeriodLabel } from "@/lib/offer";
import { API_BASE } from "@/lib/api";

const resolveMedia = (u) => (!u ? u : u.startsWith("/api/") ? `${API_BASE}${u.replace(/^\/api/, "")}` : u);

export default function ListingCard({ listing, compact = false }) {
  const { t, lang } = useI18n();
  const { has, toggle } = useFavorites();
  const isAuto = listing.sector === "auto";
  const title = lang === "en" && listing.title_en ? listing.title_en : listing.title;
  const price = new Intl.NumberFormat(lang === "fr" ? "fr-FR" : "en-US", {
    style: "currency", currency: listing.currency || "USD", maximumFractionDigits: 0,
  }).format(listing.price || 0);
  const cover = resolveMedia(listing.images?.[0]) || "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800";
  const fav = has(listing.id);
  const offerLabel = getOfferLabel(listing, lang);
  const periodLabel = getPricePeriodLabel(listing.price_period, lang);
  const surface = listing.specs?.surface_m2 ?? listing.specs?.surface_habitable_m2 ?? listing.specs?.surface_terrain_m2;
  const publicRibbon = {
    sold: lang === "fr" ? "OFFRE VENDUE" : "SOLD",
    rented: lang === "fr" ? "OFFRE LOUÉE" : "RENTED",
    negotiation: lang === "fr" ? "EN COURS DE NÉGOCIATION" : "NEGOTIATING",
    pending: lang === "fr" ? "OFFRE RÉSERVÉE" : "RESERVED",
  }[listing.status];

  const onFav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(listing.id);
  };

  return (
    <Link
      to={`/annonce/${listing.id}`}
      className={`group card-app hover:-translate-y-0.5 transition-transform ${compact ? "flex min-h-36 overflow-hidden sm:block" : "block"}`}
      data-testid={`listing-card-${listing.id}`}
    >
      <div className={`relative overflow-hidden bg-brand-mist ${compact ? "w-32 shrink-0 sm:w-auto sm:aspect-[4/3]" : "aspect-[4/3]"}`}>
        <img
          src={cover}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute left-2 top-2 flex flex-wrap gap-1 sm:left-3 sm:top-3 sm:gap-1.5">
          <StatusBadge status={listing.status} lang={lang} />
          {listing.featured === true && <StatusBadge status="featured" lang={lang} />}
        </div>
        {publicRibbon && <div className="absolute inset-x-0 bottom-0 bg-brand-navy/90 px-3 py-2 text-center text-xs font-extrabold tracking-[0.16em] text-white backdrop-blur-sm" data-testid={`listing-ribbon-${listing.id}`}>{publicRibbon}</div>}
        <button
          onClick={onFav}
          className={`absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/95 shadow-sm backdrop-blur transition-transform hover:scale-105 active:scale-95 sm:right-3 sm:top-3 sm:h-9 sm:w-9 ${fav ? "text-brand-orange" : "text-brand-navy"}`}
          data-testid={`fav-btn-${listing.id}`}
          aria-label={fav ? "Retirer des favoris" : "Ajouter aux favoris"}
        >
          <Heart className="h-4.5 w-4.5" strokeWidth={2.2} fill={fav ? "currentColor" : "none"} />
        </button>
      </div>

      <div className={`min-w-0 p-3 sm:p-4 ${compact ? "flex flex-1 flex-col justify-center" : ""}`}>
        {/* Type d'offre (Vente de villa, Location de véhicule, etc.) */}
        <div className="truncate text-[9px] font-bold uppercase tracking-[0.13em] text-brand-orange sm:text-[10px] sm:tracking-[0.18em]">{offerLabel}</div>
        {!isAuto && ["meuble", "semi_meuble"].includes(listing.specs?.ameublement) && <span className="mt-1 inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700">{listing.specs.ameublement === "meuble" ? "Meublé" : "Semi-meublé"}</span>}

        {/* Titre — apparait une seule fois */}
        <h3 className={`mt-1 font-display font-bold leading-snug text-brand-navy line-clamp-2 transition-colors group-hover:text-brand-orange sm:mt-1.5 sm:text-[15px] lg:text-base ${compact ? "text-sm" : "text-[13px]"}`}>
          {title}
        </h3>

        {/* Localisation */}
        <div className="mt-1 flex items-center gap-1 text-[10px] text-slate-500 sm:mt-1.5 sm:gap-1.5 sm:text-xs">
          <MapPin className="h-3.5 w-3.5" />
          <span className="truncate">{listing.city}{listing.zone ? ` · ${listing.zone}` : ""}</span>
        </div>

        {/* Caractéristiques */}
        <div className="mt-2 hidden items-center gap-3.5 text-xs text-slate-600 sm:flex">
          {!isAuto && listing.specs?.chambres != null && (
            <span className="flex items-center gap-1"><Bed className="h-3.5 w-3.5" /> {listing.specs.chambres}</span>
          )}
          {!isAuto && listing.specs?.sdb != null && (
            <span className="flex items-center gap-1"><Bath className="h-3.5 w-3.5" /> {listing.specs.sdb}</span>
          )}
          {surface != null && surface !== "" && (
            <span className="flex items-center gap-1"><Ruler className="h-3.5 w-3.5" /> {surface} m²</span>
          )}
          {!isAuto && listing.specs?.capacite_personnes && <span>{listing.specs.capacite_personnes} personnes</span>}
          {isAuto && listing.specs?.annee != null && (
            <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {listing.specs.annee}</span>
          )}
          {isAuto && listing.specs?.carburant && (
            <span className="flex items-center gap-1"><Fuel className="h-3.5 w-3.5" /> {listing.specs.carburant}</span>
          )}
        </div>

        {/* Prix — bloc principal */}
        <div className={`mt-2 flex items-baseline justify-between border-t border-slate-100 pt-2 sm:mt-3 sm:pt-3 ${compact ? "mt-auto" : ""}`}>
          <div className="font-display text-base font-extrabold text-brand-navy sm:text-xl" data-testid={`card-price-${listing.id}`}>
            {price}
            {listing.purpose === "rent" && <span className="text-xs font-medium text-slate-400 ml-1">{periodLabel}</span>}
          </div>
          <span className="hidden text-xs font-bold text-brand-orange sm:inline">Voir détails</span>
        </div>
      </div>
    </Link>
  );
}
